import type { Block, ChildBlock } from '../schemas/block';
import isEmptyRichText from './isEmptyRichText';

/**
 * Block helpers shared by the admin canvas editor, the public renderer, and
 * the html→blocks migration (ADR 0033).
 */

// crypto.randomUUID exists in every target runtime (browsers, Node 16.7+),
// but jsdom in some jest configs lacks it — fall back to a good-enough id.
export function newBlockId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `blk_${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 10)}`;
}

export function newBlock(type: Block['type']): Block {
  const id = newBlockId();
  switch (type) {
    case 'richtext':
      return { id, type, html: '' };
    case 'image':
      return { id, type, src: '', alt: '', width: 'full', alignment: 'center' } as Block;
    case 'embed':
      return { id, type, url: '' } as Block;
    case 'divider':
      return { id, type };
    case 'button':
      return { id, type, label: '', url: '', style: 'primary', alignment: 'left' } as Block;
    case 'columns':
      return {
        id,
        type,
        columns: [
          { id: newBlockId(), blocks: [] as ChildBlock[] },
          { id: newBlockId(), blocks: [] as ChildBlock[] },
        ],
      };
  }
}

// The migration bridge: one legacy Quill-HTML string becomes exactly one
// richtext block. Lossless by construction — the html is carried verbatim.
export function htmlToBlocks(html: string | undefined | null): Block[] {
  return [{ id: newBlockId(), type: 'richtext', html: html || '' }];
}

// The renderer contract (ADR 0033): a document is "on blocks" only when the
// array exists AND is non-empty. Absent/empty means unmigrated → render
// legacy `html`. Never test `blocks` truthiness directly — a stray [] must
// not switch a document onto the blocks path.
export function hasBlocks(entity: { blocks?: unknown[] | null } | null | undefined): boolean {
  return Array.isArray(entity?.blocks) && (entity!.blocks as unknown[]).length > 0;
}

// "Visually empty" for surfaces that hide themselves when there's nothing to
// show (the division callout). Blocks-era analogue of isEmptyRichText: only
// richtext blocks whose html is empty count as empty — any other block type
// is content.
export function blocksAreEmpty(blocks: Block[] | undefined | null): boolean {
  if (!Array.isArray(blocks) || blocks.length === 0) return true;
  return blocks.every((b) => b.type === 'richtext' && isEmptyRichText(b.html));
}
